'use client';

import { OddsButton } from '@/components/widgets/odds-button';
import { useAccountInfo } from '@/hooks/query/account';
import { betTypeService } from '@/services/bettype.service';
import { matchService } from '@/services/match.service';
import { DisplayLineInfo, getDisplayLineInfo } from '@/utils/odds-utils';
import { SportMatchModel, Market } from '@nf/types/odds';
import { itemsEmptyCheck, objectEmptyCheck, stringEmptyCheck } from '@nf/utils-common/fpw/utils';
import { BetChoice } from '../../types';
import { MatchEventWrapper } from './match-event-wrapper';
import { MatchTeams } from '../match-teams';
import { MatchInfo } from '../../match-info';
import { StatisticButton } from '@/components/feature-buttons/statistic-button';
import { ScoreItem, scoreboardService } from '@/services/scoreboard.service';
import { Box, Maybe, MaybeMatch, matchEquals } from '@nf/utils-common/fpw/monadic';
import { useTranslations } from 'next-intl';
import { BetTypeSetting, betTypesCategory } from '@/types/bet-type-settings';
import { useEffect, useMemo, useState } from 'react';
import { isDesktop, isTablet } from 'react-device-detect';
import { CashoutIcon } from '@/components/icons/cashout-icon';
import { BetTypeFilterCriterion } from '@/types/settings';
import { MatchOddsLoadingWrapper } from '../../match-odds-loading-wrapper';

interface ListItemContentProps {
	match: SportMatchModel;
	markets: Market[];
	isLoading: boolean;
	homeTeamName: string;
	awayTeamName: string;
	sport: number;
	betTypeSetting: BetTypeSetting;
	betTypeFilterCriterion: BetTypeFilterCriterion | undefined;
}
export const ListItemContent = ({
	match,
	markets,
	isLoading,
	homeTeamName,
	awayTeamName,
	sport,
	betTypeSetting,
	betTypeFilterCriterion
}: ListItemContentProps) => {
	const [market, setMarket] = useState<Market>();
	const { user } = useAccountInfo();
	const betTypeTranslate = useTranslations('BetType');
	const t = useTranslations('General');
	const teamScoreList = useMemo(() => scoreboardService.getAllSportScore(match), [match]);
	const [homeScoreList, awayScoreList] = useMemo(() => [
		scoreboardService.getMatchCricketScore(match, 'home'),
		scoreboardService.getMatchCricketScore(match, 'away')
	], [match])
	const strongTeam = matchService.getStrongTeam(match.BetTypes);

	const getFirstScoreText = (scoreList: ScoreItem[]): string => itemsEmptyCheck(scoreList)
		.chain((scoreList: ScoreItem[]) => stringEmptyCheck(scoreList[0]?.text))
		.fold(
			() => '',
			(teamScore: string) => teamScore
		)

	const [teamAScore, teamBScore] = matchEquals(sport)
		.on(50, () => [
			getFirstScoreText(homeScoreList),
			getFirstScoreText(awayScoreList)]
		)
		.on(44, () => [
			`${t('lbl_round')}${getFirstScoreText(teamScoreList)}`,
			'']
		)
		.otherwise(() => getFirstScoreText(teamScoreList).split('-'))

	useEffect(() => {
		MaybeMatch(!isLoading)
			.chain(() => objectEmptyCheck(betTypeFilterCriterion ?? {}))
			.chain(() => itemsEmptyCheck(markets ?? []))
			.fold(
				() => {},
				() => setMarket(markets.find(market => {
					const targetBetTypes = betTypeFilterCriterion?.betTypes ?? [];
					const isTargetBetType = targetBetTypes.includes(market.BetTypeId);
					const specifiedResourceId = betTypeFilterCriterion?.resourceId;
					return isTargetBetType && (!specifiedResourceId || market.Resourceid === specifiedResourceId);
				}))
			)
	}, [betTypeFilterCriterion, isLoading, markets])

	const choiceGroup: Map<string, Array<BetChoice>> = useMemo(() => {
		if (isLoading) return new Map();
		const betChoiceDisplaySetting = betTypeSetting?.betChoiceDisplaySetting ?? {};
		return objectEmptyCheck(betChoiceDisplaySetting)
			.chain(() => MaybeMatch(market?.gt === 'fc'))
			.fold(
				() => betTypeSetting.betChoices.reduce((accumulator, betChoice, index) => {
					accumulator.set(`default${index}`, new Array(betChoice));
					return accumulator;
				}, new Map() as Map<string, Array<BetChoice>>),
				() => betTypeSetting.betChoices.reduce((accumulator, betChoice) => {
					const choiceDisplaySetting = betChoiceDisplaySetting[betChoice.betTeam];
					if (choiceDisplaySetting.betTypeClassName === undefined) return accumulator;
					const addedBetChoice: BetChoice = {
						label: choiceDisplaySetting.label,
						betTeam: betChoice.betTeam
					};
					accumulator.has(choiceDisplaySetting.betTypeClassName) ?
						accumulator.get(choiceDisplaySetting.betTypeClassName)?.push(addedBetChoice) :
						accumulator.set(choiceDisplaySetting.betTypeClassName, new Array(addedBetChoice));
					return accumulator;
				}, new Map() as Map<string, Array<BetChoice>>)
			);
	}, [isLoading, betTypeSetting, market]);

	const getTranslatedLine = ({ prefixLabel, displayLine }: DisplayLineInfo) => stringEmptyCheck(prefixLabel)
		.fold(
			() => displayLine,
			(label: string) => `${t(label)}${displayLine}`
		)

	
	return (
		<>
			<MatchEventWrapper
				sport={sport}
				market={match.MaT}
				leagueId={match.LeagueId}
				matchId={match.MatchId}
			>
				<MatchTeams
					scoreA={teamAScore}
					scoreB={teamBScore}
					isLive={match.IsLive}
					homeRedCardCount={match.Rc1}
					awayRedCardCount={match.Rc2}
					teamIdA={match.TeamId1}
					teamIdB={match.TeamId2}
					teamNameA={homeTeamName}
					teamNameB={awayTeamName}
					sportId={sport}
					serveTeam={scoreboardService.getServeTeam(match, sport)}
					strongTeam={strongTeam}
				/>
				
				<MatchInfo
					match={match}
					betTypeCount={match.Moc + markets.length}
					oddsPageLayout="list"
				/>
				<div className="match-statistic">
					<StatisticButton match={match} isNeedSetMatchId={true} />
					{
						isTablet || isDesktop ?
							<CashoutIcon match={match} /> : null
					}
				</div>
			</MatchEventWrapper>
			<div className="match__bettypes">
				<MatchOddsLoadingWrapper isLoading={isLoading}>
					{
						market && choiceGroup.size > 0 && (
							<>
								{
									Maybe(betTypeSetting.betTypesCategory)
										.chain((category: betTypesCategory) => MaybeMatch(
											category === 'overRun' || 
											category === 'EarlyInningsMoneyLine' || 
											category === 'EarlyInningsRunLine' || 
											category === 'EarlyInningsTotalRuns'
										)
										)
										.chain((category: betTypesCategory) => 
											stringEmptyCheck(market.Resourceid).map((resourceId: string) => ({ category, resourceId })) 
										)
										.fold(
											() => {},
											({ category, resourceId }: { category: betTypesCategory; resourceId: string }) => {
												const betTypeTitle = betTypeTranslate.raw(`b${market.BetTypeId}`);
												if (category === 'overRun') {
													return <div className="match__bettype-title">{betTypeService.replaceCricketResource(resourceId, betTypeTitle, sport)}</div>;
												} else {
													return <div className="match__bettype-title">{betTypeService.getBetTypeReplaceTitle(betTypeTitle, sport, market)}</div>;
												}
											}
										)
								}
								{
									[...choiceGroup].map(([betTypeClass, betChoices]) => {
										return (
											<div key={betTypeClass} className="match__bettype">
												{
													betChoices.map(betChoice => {
														const selection = market.Selections[betChoice.betTeam as keyof BetChoice];
														const displayLine = Box.of({
															line: market.Line,
															betType: market.BetTypeId,
															betTeam: betChoice.betTeam,
														})
															.map(getDisplayLineInfo)
															.map(getTranslatedLine)
															.extract();
														return selection && (
															<OddsButton
																key={`${market.MatchId}_${market.MarketId}_${market.BetTypeId}_${selection.SelId}`}
																goal={displayLine}
																variant={betTypeService.getBetChoiceDisplaySettingBetTypeClassName(market.BetTypeId, betChoice.betTeam, 'vertical')}
																match={match}
																market={market}
																selection={selection}
																homeTeamName={homeTeamName}
																awayTeamName={awayTeamName}
																betTeam={selection.SelId}
																originalPrice={selection.Price}
																sport={sport}
																type="OU"
																oddsType={user?.OType}
																accountStatus={user?.ActStatus ?? 0}
															/>
														)
													})
												}
											</div>
										)
									})
								}
							</>
						)
					}
				</MatchOddsLoadingWrapper>
			</div>
		</>
	)
}